'use client'

/**
 * SectionShare — a "share this section" control for the /data surfaces
 * (owner directive 07/20, Section E item 14: "every section individually
 * shareable").
 *
 * Thin wrapper over the shared ShareBar (owner spec 07/20: ONE share icon
 * system sitewide — real official full-color logos, no chips, one line,
 * always colorful at rest). Builds a deep link to the section's anchor,
 * resolved at click time so it works with client-side navigation and cached
 * renders.
 */
import { ShareBar } from '@/components/data/share-bar'

interface SectionShareProps {
  /** The section's anchor id (the element it deep-links to). */
  anchorId: string
  /** Section title used in the share text. */
  title: string
  className?: string
}

export function SectionShare({ anchorId, title, className = '' }: SectionShareProps) {
  return (
    <ShareBar
      variant="compact"
      title={title}
      anchorId={anchorId}
      utmContent={`section-${anchorId}`}
      label="Share"
      className={className}
    />
  )
}

/**
 * ShareableSectionHeader — IndexSectionHeader's visual with a SectionShare
 * control docked on the right. Sets the section anchor and keeps the heavy
 * black rule. Drop-in replacement wherever a /data section should be shareable.
 * The header wraps (title line, then meta + share) whenever the full one-line
 * share row would not fit beside the title — zero clipping at any width.
 */
export function ShareableSectionHeader({
  id,
  title,
  meta,
}: {
  id: string
  title: string
  meta?: string
}) {
  return (
    <div
      id={id}
      className="scroll-mt-28 flex flex-wrap justify-between items-baseline gap-x-4 gap-y-2 mb-6 sm:mb-7 border-b border-gray-900 pb-3 sm:pb-4"
    >
      <h2 className="text-2xl sm:text-[28px] font-black tracking-[-0.025em] text-gray-900 m-0">
        {title}
      </h2>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0">
        {meta && (
          <span className="hidden sm:inline text-[11px] sm:text-xs uppercase tracking-[0.15em] text-gray-500">
            {meta}
          </span>
        )}
        <SectionShare anchorId={id} title={title} />
      </div>
    </div>
  )
}
